/**
 * MeetEmailModal
 * Manda al lead, por correo, el enlace de la videollamada de su demo.
 *
 * Nace del caso real del 27/7/2026: un lead pidió la invitación por email y no
 * había forma de mandarla desde el CRM. El enlace lo pega el closer (lo genera
 * él en Google Meet); la fecha viene de la demo agendada, si la hay.
 */
import React, { useEffect, useMemo, useState } from "react";
import { X, Loader2, Eye, Send, CheckCircle2, AlertTriangle, Video } from "lucide-react";
import axiosClient from "../../api/axiosClient";
import { isValidEmail, isValidMeetUrl, meetEmailErrorMessage } from "../../utils/meetEmail";
import MeetEmailPreviewModal from "./MeetEmailPreviewModal";

/** ISO → valor de <input type="datetime-local"> en hora local del navegador. */
function isoToLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export default function MeetEmailModal({ open, onClose, lead, scheduledAt, onSent }) {
  const [meetUrl, setMeetUrl] = useState("");
  const [email, setEmail] = useState("");
  const [cuando, setCuando] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [state, setState] = useState({ status: "idle", error: null });

  useEffect(() => {
    if (!open) return;
    setMeetUrl("");
    setEmail(lead?.email || "");
    setCuando(isoToLocalInput(scheduledAt));
    setState({ status: "idle", error: null });
  }, [open, lead?.email, scheduledAt]);

  const urlOk = isValidMeetUrl(meetUrl);
  const emailOk = isValidEmail(email);
  const puedeEnviar = urlOk && emailOk && state.status !== "sending";
  const isoCuando = useMemo(() => localInputToIso(cuando), [cuando]);

  const enviar = async () => {
    setState({ status: "sending", error: null });
    try {
      await axiosClient.post("/ventas/meet-email", {
        meet_url: meetUrl.trim(),
        email: email.trim(),
        ...(lead?.contact_id ? { contact_id: lead.contact_id } : {}),
        ...(lead?.nombre ? { nombre: lead.nombre } : {}),
        ...(isoCuando ? { scheduled_at: isoCuando } : {}),
      });
      setState({ status: "sent", error: null });
      onSent?.();
    } catch (err) {
      setState({ status: "error", error: meetEmailErrorMessage(err) });
    }
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-[10800] flex items-center justify-center p-3 sm:p-4">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

        <div className="relative w-full max-w-[520px] max-h-[92vh] overflow-y-auto bg-white rounded-2xl sm:rounded-3xl shadow-2xl">
          <div className="flex items-center justify-between gap-2 px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2 min-w-0">
              <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600 flex-shrink-0">
                <Video size={16} />
              </div>
              <p className="text-sm font-bold text-gray-900 truncate">
                Enviar enlace de la videollamada
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition flex-shrink-0"
            >
              <X size={16} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">
                Enlace de Google Meet
              </label>
              <input
                type="url"
                value={meetUrl}
                onChange={(e) => setMeetUrl(e.target.value)}
                placeholder="https://meet.google.com/xxx-xxxx-xxx"
                autoFocus
                className={`w-full border rounded-xl px-3 py-2.5 text-sm outline-none transition ${
                  meetUrl && !urlOk
                    ? "border-red-300 focus:border-red-400"
                    : "border-gray-200 focus:border-indigo-400"
                }`}
              />
              {meetUrl && !urlOk && (
                <p className="mt-1.5 text-xs text-red-600">
                  Debe ser un enlace de sala de Google Meet.
                </p>
              )}
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">
                Destinatario
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="correo@delcliente.es"
                className={`w-full border rounded-xl px-3 py-2.5 text-sm outline-none transition ${
                  email && !emailOk
                    ? "border-red-300 focus:border-red-400"
                    : "border-gray-200 focus:border-indigo-400"
                }`}
              />
            </div>

            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">
                Día y hora <span className="font-medium normal-case text-gray-400">(opcional)</span>
              </label>
              <input
                type="datetime-local"
                value={cuando}
                onChange={(e) => setCuando(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-indigo-400"
              />
              <p className="mt-1.5 text-xs text-gray-400">
                Si lo dejas vacío, el correo va solo con el enlace.
              </p>
            </div>

            {state.status === "error" && (
              <div className="flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{state.error}</span>
              </div>
            )}

            {state.status === "sent" && (
              <div className="flex items-start gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-700">
                <CheckCircle2 size={16} className="flex-shrink-0 mt-0.5" />
                <span>Correo enviado a {email}.</span>
              </div>
            )}

            <div className="flex items-center gap-2 pt-1">
              <button
                type="button"
                disabled={!puedeEnviar}
                onClick={enviar}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {state.status === "sending" ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
                {state.status === "sent" ? "Enviar de nuevo" : "Enviar correo"}
              </button>
              <button
                type="button"
                onClick={() => setPreviewOpen(true)}
                title="Ver cómo le llega el correo"
                className="p-2.5 rounded-xl border border-gray-200 text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition"
              >
                <Eye size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <MeetEmailPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        meetUrl={meetUrl.trim()}
        email={email}
        nombre={lead?.nombre}
        scheduledAt={isoCuando}
        contactId={lead?.contact_id}
      />
    </>
  );
}
